import { getAtRiskDealsColumns } from "@/dashboard/components/data-table";
import { type RiskIndicator } from "@/revenue-intelligence/lib/risk-contracts";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Card,
} from "@/components/ui/card";
import { Database, TODO } from "@revelate/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface AtRiskDealsDataTableProps {
  deals: TODO[];
  onClickDeal?: (dealId: number) => void;
  showTitle?: boolean;
  showTopRisk?: boolean;
  showConcentration?: boolean;
  highlightIndicator?: RiskIndicator;
  hideRiskColumn?: boolean;
  showExpectedCloseDate?: boolean;
  showForecastImpact?: boolean;
  showForecastShare?: boolean;
  showUrgency?: boolean;
  showUrgencyBadge?: boolean;
  forecastValueDisplay?: "contribution" | "impact";
  topRiskDisplay?: "top-risk" | "primary-reason";
  valueHeader?: "value" | "exposed-value";
  displayMode?: "default" | "key-forecast-deals" | "recent-loss-signals";
  companyCurrency?: Database["public"]["Tables"]["currencies"]["Row"] | null;
}

export function AtRiskDealsDataTable({
  deals,
  onClickDeal,
  showTitle = true,
  showTopRisk = false,
  showConcentration = false,
  highlightIndicator,
  hideRiskColumn = false,
  showExpectedCloseDate = false,
  showForecastImpact = false,
  showForecastShare = false,
  showUrgency = false,
  showUrgencyBadge = false,
  forecastValueDisplay = "impact",
  topRiskDisplay = "top-risk",
  valueHeader = "value",
  displayMode = "default",
  companyCurrency = null,
}: AtRiskDealsDataTableProps) {

  const table = useReactTable({
    data: deals,
    columns: getAtRiskDealsColumns({
      showTopRisk,
      showConcentration,
      highlightIndicator,
      hideRiskColumn,
      showExpectedCloseDate,
      showForecastImpact,
      showForecastShare,
      showUrgency,
      showUrgencyBadge,
      forecastValueDisplay,
      topRiskDisplay,
      valueHeader,
      displayMode,
      companyCurrency,
    }),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="min-w-0 max-w-full space-y-4 py-4">
      <Card className="min-w-0 max-w-full overflow-hidden border border-gray-300 px-5 py-2 shadow-sm">
        {showTitle && (
          <p className="py-4 font-medium text-sm">
            At-risk deals
          </p>
        )}
        <Table className={displayMode === "key-forecast-deals" ? "min-w-[980px]" : undefined}>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className="px-0 pr-5" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => onClickDeal?.(row.original.id)} className="group cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell className="px-0 pr-5" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
